/**
 * TreeGraphBackground - Canvas-based animated tournament tree visualization
 *
 * A high-performance background component that displays an animated binary tree
 * with a wave effect traveling upward, highlighting winning paths in gold
 * and fading losing paths to red.
 */

import React, { useRef, useEffect, useCallback } from "react";
import {
  generateTree,
  collectAllElements,
  validateTree,
} from "./treeGenerator";
import { selectWinners, getUltimateWinningPath } from "./tournament";
import { renderFrame } from "./canvasRenderer";
import { AnimationController, AnimationLoop } from "./animationController";
import { useCanvasResize } from "./useCanvasResize";
import {
  DEFAULT_TREE_DEPTH,
  CANVAS_OPACITY,
  BACKGROUND_GRADIENT_OPACITY,
} from "./constants";
import type { TreeNode, TreeElements, CanvasConfig } from "./types";

interface TreeGraphBackgroundProps {
  /** Depth of the binary tree (default: 6) */
  depth?: number;
  /** Whether to show performance stats (default: false) */
  debug?: boolean;
  /** Custom class name for the container */
  className?: string;
}

/**
 * Main component for the tree graph background
 */
const TreeGraphBackground: React.FC<TreeGraphBackgroundProps> = ({
  depth = DEFAULT_TREE_DEPTH,
  debug = false,
  className = "",
}) => {
  // Refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const treeRef = useRef<TreeNode | null>(null);
  const elementsRef = useRef<TreeElements>({ nodes: [], paths: [] });
  const winnersRef = useRef<Map<string, boolean>>(new Map());
  const ultimateWinnersRef = useRef<Set<string>>(new Set());
  const animationControllerRef = useRef<AnimationController | null>(null);
  const animationLoopRef = useRef<AnimationLoop | null>(null);
  const canvasConfigRef = useRef<CanvasConfig>({ width: 0, height: 0, dpr: 1 });

  // State to force animation restart on resize
  const [resizeKey, setResizeKey] = React.useState(0);

  /**
   * Generates a new tournament with random winners
   */
  const generateNewTournament = useCallback(() => {
    if (!treeRef.current) return;

    try {
      winnersRef.current = selectWinners(treeRef.current, depth);
      ultimateWinnersRef.current = getUltimateWinningPath(
        treeRef.current,
        winnersRef.current
      );
    } catch (error) {
      console.error("Failed to generate tournament:", error);
    }
  }, [depth]);

  /**
   * Handles canvas resize events
   */
  const handleResize = useCallback(
    (config: CanvasConfig) => {
      console.log("TreeGraphBackground handleResize called with:", config);
      canvasConfigRef.current = config;

      // Regenerate tree for new dimensions
      try {
        const tree = generateTree(config.width, config.height, depth);

        // Validate tree structure in debug mode
        if (debug && !validateTree(tree)) {
          console.error("Invalid tree structure generated");
          return;
        }

        treeRef.current = tree;
        elementsRef.current = collectAllElements(tree);
        winnersRef.current = selectWinners(tree, depth);
        ultimateWinnersRef.current = getUltimateWinningPath(
          tree,
          winnersRef.current
        );
        console.log(
          "Tree regenerated with",
          elementsRef.current.nodes.length,
          "nodes"
        );

        // Update animation controller with new height
        if (animationControllerRef.current) {
          animationControllerRef.current.setCanvasHeight(config.height);
        }

        // Force animation restart by changing key
        setResizeKey((prev) => prev + 1);
      } catch (error) {
        console.error("Failed to regenerate tree on resize:", error);
      }
    },
    [depth, debug]
  );

  /**
   * Render frame function - memoized with debug dependency
   */
  const renderFrameCallback = useCallback(
    (waveY: number, timestamp: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const { width, height } = canvasConfigRef.current;

      try {
        renderFrame(
          ctx,
          elementsRef.current.nodes,
          elementsRef.current.paths,
          winnersRef.current,
          ultimateWinnersRef.current,
          waveY,
          width,
          height
        );

        // Debug info
        if (debug) {
          ctx.fillStyle = "rgba(255, 255, 255, 0.8)";
          ctx.font = "12px monospace";
          ctx.fillText(`Wave Y: ${waveY.toFixed(1)}`, 10, 20);
          ctx.fillText(`Nodes: ${elementsRef.current.nodes.length}`, 10, 35);
          ctx.fillText(`Paths: ${elementsRef.current.paths.length}`, 10, 50);
          ctx.fillText(
            `Size: ${width.toFixed(0)}x${height.toFixed(0)}`,
            10,
            65
          );
        }
      } catch (error) {
        console.error("Failed to render frame:", error);
      }
    },
    [debug]
  );

  // Setup resize handler
  useCanvasResize({ canvasRef, onResize: handleResize });

  // Initialize and start animation - restart on resize
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || canvasConfigRef.current.width === 0) return;

    // Stop existing animation if any
    if (animationLoopRef.current) {
      animationLoopRef.current.stop();
    }

    // Create animation controller
    animationControllerRef.current = new AnimationController(
      canvasConfigRef.current.height,
      generateNewTournament
    );

    // Create animation loop with memoized callback
    animationLoopRef.current = new AnimationLoop(
      animationControllerRef.current,
      renderFrameCallback
    );

    // Start animation
    animationLoopRef.current.start();

    // Cleanup
    return () => {
      animationLoopRef.current?.stop();
    };
  }, [generateNewTournament, renderFrameCallback, resizeKey]); // Restart when resizeKey changes

  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`}>
      <canvas
        ref={canvasRef}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        style={{
          opacity: CANVAS_OPACITY,
          width: `${canvasConfigRef.current.width}px`,
          height: `${canvasConfigRef.current.height}px`,
        }}
      />
      <div
        className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"
        style={{ opacity: BACKGROUND_GRADIENT_OPACITY }}
      />
    </div>
  );
};

// Export the renamed component to match the original import name
export default TreeGraphBackground;

// Also export as TreeGraphBackgroundCanvas for explicit usage
export { TreeGraphBackground as TreeGraphBackgroundCanvas };
